import { SearchRegistry } from '../types';

export const individualPartnersSearchEntries: SearchRegistry = {
  // -- Fleetcor reconciliation --
  '/individual-partners/fleetcor/reconciliation-dashboard': {
    keywords: [
      'fleetcor',
      'reconciliation',
      'reconciliation dashboard',
      'fleetcor reconciliation',
      'partner reconciliation',
      'reconciliation overview',
      'job overview',
      'fleetcor dashboard',
      'multi-environment access',
      'environment selector',
      'fleetcor reconciliation process management',
      'automated daily processing',
      'csv file generation',
      'order lifecycle tracking',
      'fleetcor specifications',
    ],
    questions: [
      'How is my Fleetcor reconciliation going?',
      'Where do I see the latest reconciliation status for Fleetcor?',
      'How often does Fleetcor reconciliation run?',
      'How do I switch between staging and production Fleetcor data?',
    ],
    faqs: [
      {
        q: 'Where do I see the status of my Fleetcor reconciliation?',
        a: 'Open Reconciliation Process → Fleetcor Reconciliation → Dashboard. The dashboard summarises the automated daily processing runs, CSV file generation status and the order lifecycle of each Fleetcor transaction. Use the Environment Selector to flip between staging and production.',
      },
    ],
  },
  '/individual-partners/fleetcor/job-executions': {
    keywords: [
      'fleetcor',
      'export history',
      'job executions',
      'reconciliation jobs',
      'past exports',
      'run history',
      'job log',
      'fleetcor run history',
      'export schedule',
      'fleetcor job list',
    ],
    questions: [
      'Where do I see past Fleetcor reconciliation runs?',
      'How do I open the history of Fleetcor exports?',
      'When did the last Fleetcor export run?',
    ],
  },
  '/individual-partners/fleetcor/job-executions/execution-details': {
    keywords: [
      'fleetcor',
      'execution details',
      'export details',
      'job details',
      'reconciliation run details',
      'job log',
      'fleetcor execution',
      'job error log',
      'fleetcor job inspector',
    ],
    questions: [
      'Where do I see the details of a specific Fleetcor export run?',
      'How do I troubleshoot a failed Fleetcor reconciliation?',
      'Why did a specific Fleetcor job fail?',
    ],
  },
  '/individual-partners/fleetcor/reconciliation-files': {
    keywords: [
      'fleetcor',
      'download files',
      'reconciliation files',
      'export files',
      'csv download',
      'download reconciliation',
      'fleetcor csv',
      'fleetcor file list',
      'reconciliation file download',
    ],
    questions: [
      'How do I download the reconciliation files for Fleetcor?',
      'Where are the Fleetcor export CSVs?',
    ],
  },
  '/individual-partners/reconcilation-process/fleetcor': {
    keywords: [
      'fleetcor',
      'file format',
      'file format guide',
      'reconciliation format',
      'export format',
      'file specification',
      'csv layout',
      'fleetcor schema',
      'csv columns',
      'csv headers',
    ],
    questions: [
      'What is the file format for Fleetcor reconciliation?',
      'How are the Fleetcor reconciliation columns defined?',
      'Which separator and encoding are used in the Fleetcor CSV?',
    ],
  },

  // -- Exorigo / Upos (Poland Invoice) --
  '/individual-partners/poland-invoice/general-info': {
    keywords: [
      'poland invoice',
      'general info',
      'callback overview',
      'exorigo',
      'upos',
      'poland integration',
      'introduction',
      'fiscal poland',
      'paragon',
      'fiscal receipt poland',
      'jpk',
    ],
    questions: [
      'What is the Poland invoice integration?',
      'Where do I read the overview of the Exorigo/Upos callback APIs?',
    ],
  },
  '/individual-partners/poland-invoice/webhook-api-reference': {
    keywords: [
      'callback api',
      'webhook api',
      'webhook reference',
      'callback reference',
      'poland invoice api',
      'webhook endpoint',
      'webhook payload',
      'exorigo webhook',
      'upos webhook',
      'poland callbacks',
    ],
    questions: [
      'What does the Callback (webhook) API for Poland Invoice look like?',
      'Where is the webhook payload schema for the Exorigo/Upos callbacks?',
    ],
  },
  '/individual-partners/poland-invoice/oauth-api-reference': {
    keywords: [
      'oauth',
      'oauth2',
      'oauth api',
      'token endpoint',
      'authorization',
      'authentication',
      'poland invoice auth',
      'exorigo oauth',
      'upos oauth',
      'oauth scopes poland',
    ],
    questions: [
      'How do I authenticate against the Exorigo/Upos OAuth2 endpoint?',
      'What scopes are available in the Poland invoice OAuth API?',
    ],
  },
  '/individual-partners/poland-invoice/smart-order-api-reference': {
    keywords: [
      'smart order',
      'smart order api',
      'smartorder',
      'order api',
      'exorigo smart order',
      'exorigo orders',
      'upos smart order',
      'smartorder endpoint',
    ],
    questions: [
      'Where is the SmartOrder API reference?',
      'How do I create or update an order via SmartOrder?',
    ],
  },
  '/individual-partners/poland-invoice/eparagon-plus-api-reference': {
    keywords: [
      'eparagon',
      'e-paragon',
      'eparagon plus',
      'paragon api',
      'fiscal receipt',
      'fiscal printer api',
      'poland fiscal',
      'eparagon endpoint',
      'eparagon plus reference',
    ],
    questions: ['What is e-Paragon+?', 'Where is the e-Paragon+ API reference?'],
  },

  // -- Driver / account management (API partners with the driverAccountManagementEnabled flag) --
  // Partner names stay keywords only; questions and titles are partner-neutral.
  '/driver-account-management/api-partner/accounts/list': {
    keywords: [
      'driver list',
      'account list',
      'driver accounts',
      'api partner drivers',
      'driver overview',
      'add driver',
      'create driver',
      'external driver id',
      'external company id',
      'sub-fleet drivers',
      'pairpoint',
      'aumovio',
    ],
    questions: [
      'Where do I see all my driver accounts?',
      'How do I list drivers as an API partner?',
      'How do I find a driver by the driver ID from my system?',
      'How do I add a driver to one of my sub-fleets?',
    ],
  },
  '/driver-account-management/api-partner/accounts/details': {
    keywords: [
      'driver details',
      'account details',
      'driver profile',
      'driver info',
      'driver external ids',
      'pairpoint',
      'aumovio',
    ],
    questions: [
      'Where do I see the details of a driver?',
      'Which external IDs are stored for a driver?',
    ],
  },
  '/driver-account-management/api-partner/accounts/update': {
    keywords: [
      'update driver',
      'update account',
      'edit driver',
      'driver update',
      'modify account',
      'change driver id',
      'change company id',
      'pairpoint',
      'aumovio',
    ],
    questions: [
      'How do I update a driver account?',
      'How do I change the external driver or company ID of a driver?',
    ],
  },

  // -- Order Lookup (oilpay + allguth) --
  '/order-lookup/getting-started': {
    keywords: [
      'order lookup',
      'order lookup api',
      'getting started',
      'oilpay',
      'allguth',
      'lookup api',
      'introduction',
      'order lookup intro',
      'order lookup auth',
    ],
    questions: [
      'How do I get started with the Order Lookup API?',
      'Where is the introduction to the Oilpay/Allguth Order Lookup?',
    ],
  },
  '/order-lookup/api-reference': {
    keywords: [
      'order lookup',
      'order lookup api reference',
      'api reference',
      'lookup endpoints',
      'oilpay api',
      'allguth api',
      'order lookup endpoints',
      'order lookup schema',
    ],
    questions: [
      'Where is the Order Lookup API reference?',
      'How do I look up an order via the Order Lookup API?',
    ],
  },

  // -- Deeplink Management (freenet) --
  '/deeplink-management/deeplink-generator': {
    keywords: [
      'deeplink',
      'deeplink generator',
      'deeplink management',
      'freenet deeplink',
      'generate deeplink',
      'app deeplink',
      'url generator',
      'freenet integration',
      'freenet campaign',
      'deeplink builder',
    ],
    questions: [
      'How do I generate a deeplink for the Freenet integration?',
      'Where is the deeplink generator?',
    ],
    faqs: [
      {
        q: 'How do I create a Freenet deeplink for a campaign?',
        a: 'Open Deeplink Management → Deeplink Generator. Pick the destination, fill in the parameters, and copy the generated URL. The link routes through the ryd app with the provided context.',
      },
    ],
  },
};
